import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  LocationOn,
  AttachMoney,
  Bed,
  Bathroom,
  SquareFoot,
  CalendarToday,
  Person,
  Home,
} from '@mui/icons-material';
import { Match, MatchStatus, Property, Client } from '@/types';
import MatchScoreIndicator from './MatchScoreIndicator';
import { format } from 'date-fns';

interface MatchDetailProps {
  match: Match;
  property?: Property;
  client?: Client;
  onStatusChange?: (match: Match, newStatus: MatchStatus) => void;
  onViewProperty?: (property: Property) => void;
  onViewClient?: (client: Client) => void;
}

const MatchDetail: React.FC<MatchDetailProps> = ({
  match,
  property,
  client,
  onStatusChange,
  onViewProperty,
  onViewClient,
}) => {
  const getStatusColor = (status: MatchStatus) => {
    const colorMap: { [key: string]: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' } = {
      pending: 'default',
      sent: 'primary',
      viewed: 'secondary',
      interested: 'success',
      not_interested: 'error',
      scheduled: 'warning',
      expired: 'default',
    };
    return colorMap[status] || 'default';
  };

  const handleStatusChange = (newStatus: MatchStatus) => {
    if (onStatusChange) {
      onStatusChange(match, newStatus);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Match Details
          </Typography>
          <Chip
            label={match.status}
            color={getStatusColor(match.status)}
            size="medium"
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Created: {format((match.createdAt as any).toDate(), 'MMMM dd, yyyy')}
          {match.updatedAt && ` • Last updated: ${format((match.updatedAt as any).toDate(), 'MMMM dd, yyyy')}`}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Match Score */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Match Score
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <MatchScoreIndicator score={match.score} showBreakdown={true} size="large" />
            </CardContent>
          </Card>

          {/* Match Explanation */}
          {match.score.explanation && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Why This Match?
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {match.score.explanation}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Strengths & Weaknesses */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {match.score.strengths && match.score.strengths.length > 0 && (
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="success.main">
                      Strengths
                    </Typography>
                    <List dense>
                      {match.score.strengths.map((strength, index) => (
                        <ListItem key={index}>
                          <ListItemText primary={strength} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {match.score.weaknesses && match.score.weaknesses.length > 0 && (
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="warning.main">
                      Areas to Consider
                    </Typography>
                    <List dense>
                      {match.score.weaknesses.map((weakness, index) => (
                        <ListItem key={index}>
                          <ListItemText primary={weakness} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Status Actions */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Update Status
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {match.status === MatchStatus.PENDING || match.status === MatchStatus.VIEWED ? (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => handleStatusChange(MatchStatus.INTERESTED)}
                    >
                      Mark as Interested
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleStatusChange(MatchStatus.NOT_INTERESTED)}
                    >
                      Not Interested
                    </Button>
                  </>
                ) : match.status === MatchStatus.INTERESTED ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleStatusChange(MatchStatus.SCHEDULED)}
                  >
                    Schedule Viewing
                  </Button>
                ) : null}
              </Box>
            </CardContent>
          </Card>

          {/* Analytics */}
          {match.analytics && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Analytics
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {match.analytics.viewedAt && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Viewed At
                      </Typography>
                      <Typography variant="body1">
                        {format((match.analytics.viewedAt as any).toDate(), 'MMM dd, yyyy HH:mm')}
                      </Typography>
                    </Grid>
                  )}
                  {match.analytics.respondedAt && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Responded At
                      </Typography>
                      <Typography variant="body1">
                        {format((match.analytics.respondedAt as any).toDate(), 'MMM dd, yyyy HH:mm')}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Click Through Rate
                    </Typography>
                    <Typography variant="body1">
                      {(match.analytics.clickThroughRate * 100).toFixed(1)}%
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Conversion Rate
                    </Typography>
                    <Typography variant="body1">
                      {(match.analytics.conversionRate * 100).toFixed(1)}%
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Property Preview */}
          {property && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    <Home sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Property
                  </Typography>
                  {onViewProperty && (
                    <Button size="small" onClick={() => onViewProperty(property)}>
                      View Full Details
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {property.metadata.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary' }}>
                    <AttachMoney fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="body2">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: property.metadata.currency || 'USD',
                        minimumFractionDigits: 0,
                      }).format(property.metadata.price)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary' }}>
                    <LocationOn fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="body2">
                      {property.location.city}, {property.location.state}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, color: 'text.secondary' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Bed fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="body2">{property.metadata.bedrooms}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Bathroom fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="body2">{property.metadata.bathrooms}</Typography>
                    </Box>
                    {property.metadata.squareFootage && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SquareFoot fontSize="small" sx={{ mr: 0.5 }} />
                        <Typography variant="body2">
                          {property.metadata.squareFootage.toLocaleString()} sqft
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
                {property.metadata.images && property.metadata.images.length > 0 && (
                  <Box
                    sx={{
                      width: '100%',
                      height: 200,
                      backgroundImage: `url(${property.metadata.images[0]})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      borderRadius: 1,
                    }}
                  />
                )}
              </CardContent>
            </Card>
          )}

          {/* Client Preview */}
          {client && (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    <Person sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Client
                  </Typography>
                  {onViewClient && (
                    <Button size="small" onClick={() => onViewClient(client)}>
                      View Full Profile
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {client.profile.firstName} {client.profile.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {client.profile.email}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {client.profile.phone}
                </Typography>
                {client.preferences.priceRange && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Budget Range
                    </Typography>
                    <Typography variant="body1">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: client.preferences.priceRange.currency || 'USD',
                        minimumFractionDigits: 0,
                      }).format(client.preferences.priceRange.min)} - {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: client.preferences.priceRange.currency || 'USD',
                        minimumFractionDigits: 0,
                      }).format(client.preferences.priceRange.max)}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default MatchDetail;

